import {
  ArrowDown,
  ArrowUp,
  Clipboard,
  ClipboardPaste,
  Copy,
  Eclipse,
  Eye,
  EyeOff,
  Group,
  Lock,
  Trash2,
  Ungroup,
  Unlock
} from 'lucide-react';
import React from 'react';
import {
  ContextMenuItem,
  ContextMenuSeparator,
  ContextMenuShortcut
} from '../../../ui/context-menu';
import { useMapEditor } from '../../contexts/MapEditorContext';
import { useEditorShortcuts } from '../shortcuts/EditorShortcutContext/useEditorShortcuts';
import { ShortcutDisplay } from '../shortcuts/ShortcutDisplay';

export interface ShapeContextMenuItemsProps {
  onPasteHere: () => void;
}

export const ShapeContextMenuItems: React.FC<ShapeContextMenuItemsProps> = ({
  onPasteHere
}) => {
  const {
    contextMenuShape,
    updateShape,
    deleteShape,
    moveShape,
    setContextMenuShape,
    copySelectedShapes,
    duplicateShape,
    clipboard,
    groupSelected,
    ungroupShapes,
    selectedIds,
    setNodeStyleToolTarget,
    nodeStyleToolTarget,
    copyNodeStyleToNodeTarget
  } = useMapEditor();

  const { shortcuts } = useEditorShortcuts();

  const handleShapeAction = (action: () => void) => {
    action();
    setContextMenuShape(null);
  };

  const onGroupItems = () => {
    groupSelected();
    setContextMenuShape(null);
  };

  const onUngroupItems = () => {
    if (!contextMenuShape?.id) return;
    ungroupShapes(contextMenuShape.id);
    setContextMenuShape(null);
  };

  const handleCopyStyle = () => {
    if (contextMenuShape?.type !== 'spot') return;
    setNodeStyleToolTarget(contextMenuShape);
    setContextMenuShape(null);
  };

  const handlePasteStyle = () => {
    if (!nodeStyleToolTarget || !contextMenuShape) return;
    if (nodeStyleToolTarget.id === contextMenuShape.id) return;
    copyNodeStyleToNodeTarget(nodeStyleToolTarget?.id, contextMenuShape?.id);
  };

  if (!contextMenuShape) return null;

  return (
    <>
      {contextMenuShape.type === 'spot' && (
        <ContextMenuItem
          onSelect={() => handleShapeAction(() => handleCopyStyle())}
        >
          <Eclipse className="mr-2 h-4 w-4" />
          Copy Style
        </ContextMenuItem>
      )}
      {contextMenuShape.type === 'spot' && nodeStyleToolTarget && (
        <ContextMenuItem
          onSelect={() => handleShapeAction(() => handlePasteStyle())}
          disabled={
            !nodeStyleToolTarget?.id ||
            nodeStyleToolTarget.id === contextMenuShape.id
          }
        >
          <ClipboardPaste className="mr-2 h-4 w-4" />
          Paste Style
        </ContextMenuItem>
      )}
      <ContextMenuSeparator />
      <ContextMenuItem
        onSelect={() => handleShapeAction(() => copySelectedShapes())}
      >
        <Clipboard className="mr-2 h-4 w-4" />
        Copy
        <ContextMenuShortcut>
          <ShortcutDisplay keys={shortcuts.global.copy} />
        </ContextMenuShortcut>
      </ContextMenuItem>
      <ContextMenuItem
        onSelect={() =>
          handleShapeAction(() => duplicateShape(contextMenuShape.id))
        }
      >
        <Copy className="mr-2 h-4 w-4" />
        Duplicate
        <ContextMenuShortcut>
          <ShortcutDisplay keys={shortcuts.shapeSelection.duplicate} />
        </ContextMenuShortcut>
      </ContextMenuItem>
      <ContextMenuItem
        disabled={!clipboard?.shapes?.length}
        onSelect={onPasteHere}
      >
        <ClipboardPaste className="mr-2 h-4 w-4" />
        Paste here
      </ContextMenuItem>
      <ContextMenuSeparator />
      <ContextMenuItem
        disabled={!selectedIds.length || selectedIds.length < 2}
        onSelect={onGroupItems}
      >
        <Group className="mr-2 h-4 w-4" />
        Group items
        <ContextMenuShortcut>
          <ShortcutDisplay keys={shortcuts.global.groupSelected} />
        </ContextMenuShortcut>
      </ContextMenuItem>
      <ContextMenuItem
        disabled={contextMenuShape.type !== 'group'}
        onSelect={onUngroupItems}
      >
        <Ungroup className="mr-2 h-4 w-4" />
        Ungroup items
        <ContextMenuShortcut>
          <ShortcutDisplay keys={shortcuts.global.unGroupSelected} />
        </ContextMenuShortcut>
      </ContextMenuItem>
      <ContextMenuSeparator />
      <ContextMenuItem
        onSelect={() =>
          handleShapeAction(() =>
            updateShape(contextMenuShape.id, {
              hidden: !contextMenuShape.hidden
            })
          )
        }
      >
        {contextMenuShape.hidden ? (
          <>
            <Eye className="mr-2 h-4 w-4" />
            Show
          </>
        ) : (
          <>
            <EyeOff className="mr-2 h-4 w-4" />
            Hide
          </>
        )}
        <ContextMenuShortcut>
          <ShortcutDisplay keys={shortcuts.shapeSelection.showHide} />
        </ContextMenuShortcut>
      </ContextMenuItem>
      <ContextMenuItem
        onSelect={() =>
          handleShapeAction(() =>
            updateShape(contextMenuShape.id, {
              locked: !contextMenuShape.locked
            })
          )
        }
      >
        {contextMenuShape.locked ? (
          <>
            <Unlock className="mr-2 h-4 w-4" />
            Unlock
          </>
        ) : (
          <>
            <Lock className="mr-2 h-4 w-4" />
            Lock
          </>
        )}
        <ContextMenuShortcut>
          <ShortcutDisplay keys={shortcuts.shapeSelection.lockUnlock} />
        </ContextMenuShortcut>
      </ContextMenuItem>
      <ContextMenuSeparator />
      <ContextMenuItem
        onSelect={() =>
          handleShapeAction(() => moveShape(contextMenuShape.id, 'up'))
        }
      >
        <ArrowUp className="mr-2 h-4 w-4" />
        Move front
        <ContextMenuShortcut>
          <ShortcutDisplay keys={shortcuts.shapeSelection.moveToBack} />
        </ContextMenuShortcut>
      </ContextMenuItem>
      <ContextMenuItem
        onSelect={() =>
          handleShapeAction(() => moveShape(contextMenuShape.id, 'down'))
        }
      >
        <ArrowDown className="mr-2 h-4 w-4" />
        Move back
        <ContextMenuShortcut>
          <ShortcutDisplay keys={shortcuts.shapeSelection.moveToFront} />
        </ContextMenuShortcut>
      </ContextMenuItem>
      <ContextMenuSeparator />
      <ContextMenuItem
        onSelect={() =>
          handleShapeAction(() => deleteShape(contextMenuShape.id))
        }
        className="text-red-600"
      >
        <Trash2 className="mr-2 h-4 w-4" />
        Delete
        <ContextMenuShortcut>
          <ShortcutDisplay keys={shortcuts.shapeSelection.delete} />
        </ContextMenuShortcut>
      </ContextMenuItem>
    </>
  );
};
